/**
 * SolutionTypeSelector (v2) — Cascada N-niveles basada en /v1/solution-tree
 * ─────────────────────────────────────────────────────────────────────────
 * Sustituye el viejo selector rígido (tech/parent/tipo) por una cascada
 * dinámica: cada select muestra los hijos del nodo seleccionado en el
 * nivel anterior. Termina cuando el usuario elige una hoja.
 *
 * Props:
 *   value: { id, name, path[] } | null
 *   onChange: (leaf) => void  · leaf incluye id, code=nodeId, name (nombre hoja), path (array de nombres de la ruta), parent_type (legacy compat inferido de la ruta).
 */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { ChevronRight, GitBranch, Loader2 } from "lucide-react";

export default function SolutionTypeSelector({ value, onChange }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  // path = nodos seleccionados de raíz a profundidad, incluye el nodo current del último nivel abierto
  const [chain, setChain] = useState([]); // list of node objects

  useEffect(() => {
    setLoading(true);
    api.get("/v1/solution-tree")
      .then((r) => setTree(r.data.tree || []))
      .catch(() => setTree([]))
      .finally(() => setLoading(false));
  }, []);

  // Rehidratar chain desde value.id cuando se carga
  useEffect(() => {
    if (!value?.id || tree.length === 0 || chain.length > 0) return;
    // buscar la ruta desde raíz al nodo con ese id
    const findPath = (nodes, targetId, acc = []) => {
      for (const n of nodes) {
        const next = [...acc, n];
        if (n.id === targetId) return next;
        if (n.children?.length) {
          const found = findPath(n.children, targetId, next);
          if (found) return found;
        }
      }
      return null;
    };
    const p = findPath(tree, value.id);
    if (p) setChain(p);
  }, [value?.id, tree, chain.length]);

  // Levels a mostrar: cada uno es la lista de opciones que se puede elegir en esa profundidad
  const levels = useMemo(() => {
    const out = [];
    let current = tree;
    for (let d = 0; d <= chain.length; d++) {
      if (!current || current.length === 0) break;
      out.push({
        options: current,
        selectedId: chain[d]?.id || "",
        depth: d,
      });
      current = chain[d]?.children || [];
    }
    return out;
  }, [tree, chain]);

  const changeAt = (depth, nodeId) => {
    const opts = levels[depth]?.options || [];
    const node = opts.find((o) => o.id === nodeId);
    if (!node) {
      // reset este nivel
      setChain(chain.slice(0, depth));
      if (depth === 0) onChange?.(null);
      return;
    }
    // truncar en profundidad y agregar el nuevo
    const newChain = [...chain.slice(0, depth), node];
    setChain(newChain);
    // Si es hoja → notificar al padre
    if (!node.children || node.children.length === 0) {
      const pathNames = newChain.map((n) => n.name);
      // Inferir parent_type legacy desde el nombre del nivel 1
      const lvl1 = (newChain[1]?.name || "").toLowerCase();
      const parentTypeMap = [
        ["instalac", "instalacion"], ["traslado", "traslado"], ["asistencia mayor", "asistencia_mayor"],
        ["asistencia menor", "asistencia_menor"], ["mantenimiento preventivo", "mantenimiento_preventivo"],
        ["mantenimiento correctivo", "mantenimiento_correctivo"], ["retiro", "retiro"],
        ["cambio", "cambio_equipo"], ["devolver", "devolver_equipo"], ["inspeccion", "inspeccion_tecnica"],
        ["montaje", "montaje"], ["falla", "mantenimiento_correctivo"], ["eléctric", "mantenimiento_correctivo"],
      ];
      let parent_type = "instalacion";
      for (const [k, v] of parentTypeMap) if (lvl1.includes(k)) { parent_type = v; break; }
      onChange?.({
        id: node.id,
        code: node.id,
        name: node.name,
        path: pathNames,
        parent_type,
      });
    }
  };

  const selectedFullName = chain.length ? chain.map((n) => n.name).join(" › ") : "";
  const isComplete = chain.length > 0 && (chain[chain.length - 1].children || []).length === 0;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader2 size={16} className="animate-spin" /> Cargando jerarquía…
      </div>
    );
  }
  if (tree.length === 0) {
    return (
      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
        Sin nodos en el árbol de tipos de solución. Ve a <b>Tipos de Solución</b> → <b>Re-importar Excel</b>.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="solution-type-selector">
      <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
        <GitBranch size={12} /> Jerarquía · elige de izquierda a derecha
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {levels.map((lvl) => (
          <select
            key={lvl.depth}
            value={lvl.selectedId}
            onChange={(e) => changeAt(lvl.depth, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            data-testid={`solution-level-${lvl.depth}`}
          >
            <option value="">
              {lvl.depth === 0 ? "Categoría principal…" : lvl.depth === 1 ? "Tipo…" : `Nivel ${lvl.depth + 1}…`}
            </option>
            {lvl.options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}{o.children?.length ? ` (${o.children.length})` : ""}
              </option>
            ))}
          </select>
        ))}
      </div>
      {selectedFullName && (
        <div className={`text-xs px-3 py-2 rounded flex items-center gap-2 ${isComplete ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="solution-selected-path">
          <ChevronRight size={12} className="flex-shrink-0" />
          <span className="flex-1 font-medium">{selectedFullName}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest">
            {isComplete ? "✓ Completo" : "Selecciona el siguiente nivel"}
          </span>
        </div>
      )}
    </div>
  );
}
